import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { useSelector } from "react-redux";

import { useAnchorScrollHandler } from "./useAnchorScrollHandler";
import { resolve } from "../Routes/RoutesContext";

// This component is responsible for resetting or restoring the scroll position between
// page changes
//
const useScrollManager = ({ preventScroll }) => {
  // Use the current location from the Redux store to track page changes so it happens
  // when the page data is loaded and the components are rendered. This ensures that the
  // scroll restoration happens at the right time, and not too early when the route
  // changes but the page is not yet loaded.
  const currentLocation = useSelector(({ location }) => location.currentLocation) || {};
  const previousLocation = useSelector(({ location }) => location.previousLocation) || {};
  const lastNonModalLocation =
    useSelector(({ location }) => location.lastNonModalLocation) || {};

  const { pathname, hash, search } = currentLocation;
  const previousLocationPathname = previousLocation?.pathname;
  const lastNonModalLocationRef = useRef(lastNonModalLocation);

  lastNonModalLocationRef.current = lastNonModalLocation;

  const currentRoute =
    (currentLocation?.pathname && resolve(currentLocation.pathname)) || {};

  // This checks whether the current and previous locations are from the same routesGroup,
  // in which case we'd rather not update the scroll position when the location changes
  // as both routes are from a sub-app that should maintain its scroll position accross
  // page changes.
  const isFromSameRouteGroup =
    currentRoute.routesGroup &&
    previousLocation.routesGroup &&
    currentRoute.routesGroup === previousLocation.routesGroup;

  const scrollYCache = useRef({});

  // This callback allows registering the scroll position of the current page, indexed by
  // pathname, so it can be used when the page changed with the pathname of the
  // previousLocation.
  const registerScrollY = useCallback(() => {
    if (!pathname) return;

    scrollYCache.current[pathname] = window.scrollY;
  }, [pathname]);

  useEffect(() => {
    window.addEventListener("scroll", registerScrollY);
    return () => window.removeEventListener("scroll", registerScrollY);
  }, [registerScrollY]);

  const triggerScrollToAnchorOnContentLoaded = useAnchorScrollHandler(hash);

  const restoreScrollTo = useCallback(
    scrollY => {
      // There are 2 setTimeout here to handle two different cases :
      //   - When the page is already loaded (it was visited before), the rendering is
      //     really fast, and the first "instant" timeout avoids the page to jump
      //   - The second timeout allows handling contents that were not already loaded,
      //     and that take some time to be rendered, which ensures that the scroll is
      //     restored even if the first timeout was triggered too early
      setTimeout(() => window.scrollTo(0, scrollY), 0);
      setTimeout(() => window.scrollTo(0, scrollY), 100);

      // Store the scrollY when it was restored, to allow the user to change the page
      // without scrolling, but keeping the scrollY position.
      scrollYCache.current[pathname] = scrollY;
    },
    [pathname]
  );

  // The component restores the scroll position to the top whenever there's an
  // actual page change that occured when the route changed.
  //
  // This excludes modals opening and closing, and reloading the same page.
  //
  // If the previous and new page locations are from the same routes group, it tries to
  // restore the scrollY position when the content is loaded.
  //
  useLayoutEffect(() => {
    const isModalClosing =
      previousLocation?.isModal &&
      lastNonModalLocationRef.current &&
      pathname === lastNonModalLocationRef.current.pathname;

    const previousScrollY = scrollYCache.current[previousLocationPathname];

    if (
      !hash &&
      (!pathname ||
        preventScroll ||
        isModalClosing ||
        (isFromSameRouteGroup && !previousLocationPathname))
    ) {
      return;
    }

    if (hash) {
      triggerScrollToAnchorOnContentLoaded();
    } else if (isFromSameRouteGroup) {
      restoreScrollTo(previousScrollY);
    } else {
      window.scrollTo(0, 0);
    }

    // Clean the scrollY cache so it doesn't grow as the user navigates beteen pages
    if (previousLocationPathname) delete scrollYCache.current[previousLocationPathname];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, hash, search]);
};

export default useScrollManager;
